import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import type { OntologicalSchemaEntry } from '@/services/api';

interface UOSClassGridProps {
  schema: Record<string, OntologicalSchemaEntry>;
  distribution: Record<string, number>;
}

export function UOSClassGrid({ schema, distribution }: UOSClassGridProps) {
  const entries = Object.entries(schema).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-sm text-slate-400 text-center">
        No UOS schema data available.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {entries.map(([key, entry]) => {
        const count = distribution[key] ?? 0;
        return (
          <Link
            key={key}
            to={`/knowledge/estate/entities?uos_class=${encodeURIComponent(key)}`}
            className="group flex flex-col bg-white rounded-xl border border-slate-200 p-4 hover:border-brand-teal/50 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-navy-900 truncate">
                  {entry.label || key}
                </h3>
                <div className="text-[10px] font-mono text-slate-400 mt-0.5 truncate">
                  {key}
                </div>
              </div>
              <div
                className={clsx(
                  'text-[10px] font-bold px-1.5 py-0.5 rounded border',
                  count > 0
                    ? 'bg-brand-teal/10 text-brand-teal-dark border-brand-teal/20'
                    : 'bg-slate-50 text-slate-400 border-slate-200',
                )}
              >
                {count}
              </div>
            </div>
            {entry.grounding && (
              <div className="text-[10px] text-slate-500 italic mt-1">
                grounding: {entry.grounding}
              </div>
            )}
            {entry.description && (
              <p className="text-xs text-slate-600 mt-2 line-clamp-3">
                {entry.description}
              </p>
            )}
            {entry.example_concepts && entry.example_concepts.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {entry.example_concepts.slice(0, 3).map((ex) => (
                  <span
                    key={ex}
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
                  >
                    {ex}
                  </span>
                ))}
              </div>
            )}
          </Link>
        );
      })}
    </div>
  );
}
